import { textToHtml } from '../utils/emailFormatting.js';

export const generateArtistOutreach = (artistData) => {
  const outreachReport = artistData.outreach_report;
  const artistName = artistData.name || artistData.artists_data?.name || outreachReport?.artist_name;
  const artistEmail = artistData.email || artistData.artists_data?.email || 'N/A';

  const descriptorKeywords = [
    'radio edit',
    'live',
    'performance',
    'acoustic',
    'remix',
    'mix',
    'version',
    'instrumental',
    'demo',
    'voice',
    'bonus track',
    'deluxe',
    'extended',
    'karaoke',
    'feat',
    'featuring'
  ];

  const looksLikeDescriptor = value => {
    if (!value) return false;
    const normalized = value.toLowerCase().trim();
    return descriptorKeywords.some(keyword => normalized.includes(keyword));
  };

  const sanitizeTrackTitle = raw => {
    if (!raw) return null;
    let result = String(raw).trim();
    if (!result) return null;

    // Remove descriptor suffixes after dashes (keep looping for stacked labels)
    while (result.includes(' - ')) {
      const lastDash = result.lastIndexOf(' - ');
      const suffix = result.slice(lastDash + 3).trim();
      if (looksLikeDescriptor(suffix)) {
        result = result.slice(0, lastDash).trim();
        continue;
      }
      break;
    }

    const stripDescriptorBlock = (pattern) =>
      result.replace(pattern, (match, inner) =>
        looksLikeDescriptor(inner) ? '' : match
      );

    // Remove descriptor parentheses / brackets
    result = stripDescriptorBlock(/\(([^)]+)\)/g);
    result = stripDescriptorBlock(/\[([^\]]+)\]/g);

    result = result.replace(/\s{2,}/g, ' ').trim();
    return result || raw.trim();
  };

  const rawTrackTitle =
    outreachReport?.primary_track_title ||
    artistData.tracks_data?.top_track ||
    artistData.tracks_data?.name ||
    'your music';
  const trackTitle = sanitizeTrackTitle(rawTrackTitle) || rawTrackTitle;
  const trackLabel = `*${trackTitle}*`;

  const emotionWordMap = {
    dark: 'darkness',
    darkness: 'darkness',
    aggressive: 'aggression',
    aggression: 'aggression',
    intense: 'intensity',
    intensity: 'intensity',
    power: 'power',
    powerful: 'power',
    tenderness: 'tenderness',
    tender: 'tenderness',
    nostalgic: 'nostalgia',
    nostalgia: 'nostalgia',
    wonder: 'wonder',
    peacefulness: 'peacefulness',
    peaceful: 'peacefulness',
    joyful: 'joyfulness',
    joyfulness: 'joyfulness',
    sadness: 'sadness',
    tension: 'tension',
    transcendence: 'transcendence',
    bliss: 'bliss',
    blissful: 'bliss',
    gritty: 'grit',
    grit: 'grit',
    energetic: 'energy',
    energy: 'energy'
  };

  const normalizeEmotionWord = value => {
    if (!value) return '';
    const cleaned = value.toLowerCase().replace(/\s+/g, ' ').trim();
    return emotionWordMap[cleaned] || cleaned;
  };

  const formatEmotionDescriptor = combo => {
    if (!combo || typeof combo !== 'string') return null;
    const parts = combo
      .split('+')
      .map(token => token.replace(/\*/g, '').trim())
      .filter(Boolean);
    if (!parts.length) return null;
    const normalized = parts.map(normalizeEmotionWord);
    if (normalized.length === 1) return normalized[0];
    if (normalized.length === 2) return `${normalized[0]} and ${normalized[1]}`;
    const lead = normalized.slice(0, 2).join(' and ');
    const undertones = normalized.slice(2).join(' and ');
    return `${lead} with undertones of ${undertones}`;
  };

  const emotionSignature =
    artistData.gems_data?.emotional_signature?.trim() ||
    formatEmotionDescriptor(outreachReport?.emotion_combo) ||
    formatEmotionDescriptor(artistData.emotion_combo);
  const emotionDescriptor = emotionSignature || 'that emotional palette we chase for breakout artists';
  const sanitizeReference = name => {
    if (!name) return null;
    return name.replace(/[.\s]+$/g, '').trim();
  };
  const sanitizeLeverSummary = text => {
    if (!text) return '';
    const normalizedText = text
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\n');
    const parenPattern = /\s*\([^()]*\)/g;
    return normalizedText
      .split('\n')
      .map(line => {
        const trimmed = line.trim();
        if (!trimmed) return null;

        let cleaned = trimmed;
        let previous;
        do {
          previous = cleaned;
          cleaned = cleaned.replace(parenPattern, '');
        } while (cleaned !== previous);

        cleaned = cleaned.trim().replace(/\s{2,}/g, ' ');
        if (!cleaned) return null;

        if (cleaned.endsWith('.')) {
          cleaned = cleaned.slice(0, -1);
        }

        const segments = cleaned.split(' — ');
        if (segments.length > 2) {
          const first = segments.shift();
          cleaned = `${first} — ${segments.join(', ')}`;
        }

        if (cleaned.startsWith('•')) {
          cleaned = cleaned.replace(/^•\s*/, '• ');
        }

        return cleaned;
      })
      .filter(Boolean)
      .join('\n');
  };

  const referencePrimary = sanitizeReference(artistData.reference_artist_name);
  const referenceSecondary = sanitizeReference(artistData.reference_artist_2_name);

  const formatPossessive = name => {
    if (!name) return '';
    const lower = name.toLowerCase();
    if (lower.endsWith("'s")) return name;
    if (name.endsWith('s')) return `${name}'`;
    return `${name}'s`;
  };

  let referencePhrase = 'the artists we benchmark you against at this stage';
  if (referencePrimary && referenceSecondary) {
    referencePhrase = `early ${referencePrimary} and ${formatPossessive(referenceSecondary)} work`;
  } else if (referencePrimary) {
    referencePhrase = `early ${formatPossessive(referencePrimary)} work`;
  } else if (referenceSecondary) {
    referencePhrase = `early ${formatPossessive(referenceSecondary)} work`;
  }

  const heroLineDescriptor = emotionDescriptor || 'that emotional palette we chase for breakout artists';
  const heroLine = `I've been listening to ${trackLabel} on repeat — it has ${heroLineDescriptor} I've only heard in ${referencePhrase}.`;

  const {
    listener_count,
    follower_count,
    listener_to_follower_percentile,
    additional_fans_top25,
    potential_revenue_uplift
  } = outreachReport || {};

  const listenerCount =
    listener_count ??
    artistData.spotify_monthly_listeners ??
    artistData.artists_data?.spotify_monthly_listeners;
  const followerCount =
    follower_count ??
    artistData.spotify_followers ??
    artistData.artists_data?.spotify_followers;
  const percentile =
    listener_to_follower_percentile ??
    artistData.listener_to_follower_percentile;
  const additionalFollowers = additional_fans_top25 ?? artistData.additional_fans_top25;
  const potentialFanValue =
    potential_revenue_uplift ??
    outreachReport?.additional_revenue_top25 ??
    artistData.potential_revenue_uplift ??
    artistData.additional_revenue_top25;

  const formatCompact = value => {
    if (value === null || value === undefined) return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    if (Math.abs(num) >= 1000) {
      const compact = num / 1000;
      const rounded = Math.abs(compact) >= 10 ? Math.round(compact) : parseFloat(compact.toFixed(1));
      return `${rounded} K`;
    }
    return num.toLocaleString();
  };

  const percentileDisplay = percentile ? `${percentile} %` : '25 %';
  const listenersText = formatCompact(listenerCount) || 'your current';
  const followersText = formatCompact(followerCount) || 'your current';
  const extraFollowersText =
    Number(additionalFollowers) > 0 ? formatCompact(additionalFollowers) : 'the next wave of';

  const formattedFanValue =
    Number(potentialFanValue) > 0 ? formatCompact(potentialFanValue) : null;
  const fanValueText = formattedFanValue ? `$${formattedFanValue}` : null;
  const fanValueClause = fanValueText
    ? `${fanValueText} in potential fan value`
    : 'meaningful fan value gains';
  const fanValuePrefix = fanValueText ? 'around ' : '';

  const scenario = outreachReport?.scenario || artistData.outreach_report?.scenario || artistData.scenario || null;

  const upliftLineByScenario = (() => {
    switch (scenario) {
      case 'niche_dominator':
        return `You're at about ${listenersText} monthly listeners and ${followersText} followers — already outperforming most artists in your lane. With a few focused refinements, that same audience could widen quickly, bringing ${extraFollowersText} followers and ${fanValuePrefix}${fanValueClause} without touching what makes the sound yours.`;
      case 'over_performer':
        return `You're sitting near ${listenersText} monthly listeners with about ${followersText} followers — just shy of the top ${percentileDisplay} conversion mark for your genre. Tightening that ratio alone could push you into the upper tier, translating to about ${extraFollowersText} additional followers and ${fanValuePrefix}${fanValueClause}.`;
      case 'gap_artist':
        return `You're at roughly ${listenersText} monthly listeners and ${followersText} followers — the kind of early signal we see right before wider discovery. Closing that small conversion gap could mean another ${extraFollowersText} followers and ${fanValuePrefix}${fanValueClause} as the project breaks through its next phase.`;
      default:
        return `You're sitting around ${listenersText} monthly listeners with about ${followersText} followers — just under the top ${percentileDisplay} listener-to-follower conversion in your genre. Closing that gap could add roughly ${extraFollowersText} more followers and ${fanValuePrefix}${fanValueClause} if even a fraction convert.`;
    }
  })();

  if (!outreachReport) {
    console.warn('⚠️ No production outreach report found for artist', artistData.artist_id);

    const content = `Hi ${artistName},

${heroLine}

${upliftLineByScenario}

I’m an award-winning producer and mixer working out of Freshly Baked Studios in Brooklyn. I work with artists and labels to translate this data into production adjustments that help songs connect more consistently.

I’d love to show you a complimentary mix or production pass on ${trackLabel} even though it's been out a while, or on whatever you’re finishing next, so you can hear how those refinements translate in practice.

Want me to send a Dropbox link so you can upload the stems or next track safely? That way I can run the pass and send a direct A/B for reference.

Let’s line something up when you’re ready.

Cheers,

Alexander Almgren
(917)-719-1555
Producer / Mixer / Master-er
https://freshlybakedstudios.com
instagram.com/alexanderalmgren`;

    return {
      subject: `Quick Flag — ${artistName}`,
      from: "almgren@freshlybakedstudios.com",
      to: artistEmail,
      cc: "goldfarb@freshlybakedstudios.com",
      content,
      htmlContent: textToHtml(content)
    };
  }

  const {
    subject_line,
    lever_summary_text,
    primary_track_title,
    emotion_combo,
    levers_highlighted_count
  } = outreachReport;

  const trackTitleReport = primary_track_title ? `*${primary_track_title}*` : trackLabel;
  const sanitizedLeverSummary =
    sanitizeLeverSummary(lever_summary_text) || lever_summary_text?.trim() || '';
  const leverIntroMap = {
    niche_dominator: `I ran ${trackLabel} through our system that lines it up next to other high-converting artists in your tier, sonic, and emotional space to see which levers they're using to tune up their sound.`,
    over_performer: `I ran ${trackLabel} through our system that lines it up against the top-converting peers in your tier, sonic, and emotional space to see which levers are helping them stay ahead.`,
    gap_artist: `I ran ${trackLabel} through our system that lines it up next to artists who've recently broken through in your tier, sonic, and emotional space to see which levers are pushing their sound forward.`
  };

  const leverActionLine = `If we tuned up your production/mix around those levers, we'd:`;

  const leverIntroLine = leverIntroMap[scenario] || leverIntroMap.gap_artist;

  const contentParts = [
    `Hey ${artistName},`,
    '',
    heroLine,
    '',
    upliftLineByScenario,
    '',
    leverIntroLine,
    ''
  ];

  contentParts.push(
    leverActionLine,
    '',
    sanitizedLeverSummary,
    '',
    "It's about how tone, space, and dynamics move the emotion through the mix so listeners feel it subconsciously.",
    '',
    `I’m an award-winning producer and mixer working out of Freshly Baked Studios in Brooklyn. I work with artists and labels to translate this data into production adjustments that help songs connect more consistently. Credits include Bondax, Brand New, Vanessa Carlton, Nipsey Hussle, and Dar Williams, with nearly 12B streams worldwide.`,
    '',
    `I’d love to show you a complimentary mix or production pass on ${trackLabel} even though it's been out a while, or on whatever you’re finishing next, so you can hear how those refinements translate in practice.`,
    '',
    `Want me to send a Dropbox link so you can upload the stems or next track safely? That way I can run the pass and send a direct A/B for reference.`,
    '',
    `Let’s line something up when you’re ready.`,
    '',
    'Cheers,',
    '',
    'Alexander Almgren',
    '(917)-719-1555',
    'Producer / Mixer / Master-er',
    'https://freshlybakedstudios.com',
    'instagram.com/alexanderalmgren'
  );

  const formattedContent = contentParts.join('\n');

  const htmlContent = textToHtml(formattedContent);

  return {
    subject: subject_line,
    from: "almgren@freshlybakedstudios.com",
    to: artistEmail,
    cc: "goldfarb@freshlybakedstudios.com",
    content: formattedContent,
    htmlContent
  };
};
